import { StaticImage } from 'gatsby-plugin-image';
import React, { useEffect } from 'react';
import { useStore } from '../../store/store';
import './download-app-popup.scss';
import { UserRoles, auth, logAnalyticsEvent, updateUserData } from '../../../firebaseApp';
import { handleSongPlayClickRedirect, markAppDownloadedInMailchimp, openCustomProtocolUrl } from '../authentication/utils';
import { isSupportedDesktopOs, isWindowsOs } from '../../helpers/general';
import Show from '../show/show';
import { navigate } from 'gatsby';
import ReusableDialog from './reusable-dialog';
import { GetMobileAppFromBothAppStores } from '../get-mobile-app-from-both-app-stores/get-mobile-app-from-both-app-stores';

const block = 'download-app-popup';
const windowsLink = 'https://github.com/ampliomusic/releases/releases/latest/download/AmplioInstaller.exe';
const macLink = 'https://github.com/ampliomusic/releases/releases/latest/download/AmplioInstaller.dmg';

export const DownloadAppPopup = () => {
  const [
    showDownloadPopup,
    showFailureToOpenAppDialog,
    userData,
    redirectAfterLogin,
    currentSong,
    changeStoreState,
  ] = useStore((state) => [
    state.showDownloadPopup,
    state.showFailureToOpenAppDialog,
    state.userData,
    state.redirectAfterLogin,
    state.currentSong,
    state.changeStoreState,
  ]);

  const userMightAlreadyHaveApp = (userData?.hasDownloadedApp || false) && !showFailureToOpenAppDialog;
  const closeDialogPopup = () => {
    changeStoreState('showDownloadPopup', false);
    changeStoreState('showFailureToOpenAppDialog', false);
    changeStoreState('redirectAfterLogin', null);
  };
  const [ suggestOpenApp, setSuggestOpenApp ] = React.useState(userMightAlreadyHaveApp);
  const [ downloadAnyway, setDownloadAnyway ] = React.useState(false);

  useEffect(() => {
    setSuggestOpenApp(userMightAlreadyHaveApp);
  }, [ userMightAlreadyHaveApp ]);

  const onTermsAgree = async () => {
    if (auth.currentUser) {
      closeDialogPopup();

      // Verify the user's account is set up, if not, show the account setup dialog
      if (!userData || !userData.isAccountSetup) {
        // The user needs to finish setting up their account
        changeStoreState('showNeedsAccountSetupDialog', true);
        return;
      }

      // Open the download link in a new window
      const link = isWindowsOs() ? windowsLink : macLink;
      window.open(link, '_blank');
      logAnalyticsEvent('download_app');

      // Mark that the user has downloaded the app in their account
      const newUserData = { ...userData };
      const firstTimeDownload = !userData?.hasDownloadedApp;

      newUserData.hasDownloadedApp = true;
      newUserData.agreedToDownloadTerms = true;
      await updateUserData(auth.currentUser, newUserData);
      // We don't want to subscribe developers or testers to the mailing list
      if (!userData.roles?.includes(UserRoles.Developer)
          && !userData.roles?.includes(UserRoles.Tester)
          && !userData.roles?.includes(UserRoles.TeamMember)) {
        await markAppDownloadedInMailchimp(userData.email);
      }

      // If this is the user's first time downloading the app, send them to the
      // get started page
      if (firstTimeDownload && !redirectAfterLogin) {
        navigate('/get-started/');
      } else if (redirectAfterLogin && !showFailureToOpenAppDialog) {
        handleSongPlayClickRedirect(redirectAfterLogin, currentSong);
        changeStoreState('redirectAfterLogin', null);
      }
    } else {
      // If not logged in, show the signup prompt
      changeStoreState('showDownloadPopupAfterSignup', true);
      changeStoreState('showSignupPopup', true);
      changeStoreState('showDownloadPopup', false);
      logAnalyticsEvent('attempted_app_download');
    }
  };

  const closeAndReset = () => {
    closeDialogPopup();
    setSuggestOpenApp(userMightAlreadyHaveApp);
  };

  const onDisagree = () => {
    // TODO do we need to update the user's information if they disagree?
    closeAndReset();
    logAnalyticsEvent('disagree_to_download_app');
  };

  const onCancelDialog = () => {
    closeAndReset();
    setDownloadAnyway(false);
  };

  const onOpenApp = async () => {
    closeAndReset();
    await openCustomProtocolUrl();
  };

  if (suggestOpenApp && isSupportedDesktopOs()) {
    return (
      <ReusableDialog open={showDownloadPopup} onClose={onCancelDialog}>
        <h4 className={`${block}__dialog-title`}>Open the Amplio App!</h4>
        <h5 className={`${block}__dialog-subtitle`}>
            It looks like you might already have the Amplio software. Open it to play songs!
        </h5>
        <div className={`${block}__buttons-wrap`}>
          <button className={`${block}__agree-button secondary`} onClick={() => setSuggestOpenApp(false)}>
              Download Again
          </button>
          <button className={`${block}__agree-button`} onClick={onOpenApp}>
              Open Amplio
          </button>
        </div>
      </ReusableDialog>
    );
  }

  return (
    <ReusableDialog open={showDownloadPopup || showFailureToOpenAppDialog} onClose={onCancelDialog}>
      <Show when={!isSupportedDesktopOs() && !downloadAnyway}>
        <h4 className={`${block}__dialog-title`}>
            It looks like you're on mobile!
        </h4>
        <h5 className={`${block}__dialog-subtitle`}>
            Get the the app from the stores below.
        </h5>
        <GetMobileAppFromBothAppStores compact style={{ marginBottom: '1rem' }} />
        <div className={`${block}__links-wrap`}>
          <a href="#" onClick={(e) => { e.preventDefault(); setDownloadAnyway(true); }}>
              Download the desktop app
          </a>
          <a href="#" onClick={(e) => {
            e.preventDefault();
            onCancelDialog();
            onOpenApp();
          }}>
              Open the desktop app
          </a>
        </div>
      </Show>
      <Show when={isSupportedDesktopOs() || downloadAnyway}>
        <Show when={showFailureToOpenAppDialog}>
          <h4 className={`${block}__dialog-title`}>
              We couldn't tell if that worked to open the app
          </h4>
          <h5 className={`${block}__dialog-subtitle`}>
              If you have the app installed, please <a href={redirectAfterLogin} onClick={(e) => {
              e.preventDefault();
              openCustomProtocolUrl(redirectAfterLogin);
              onCancelDialog();
            }} about='_blank'>
                click here to try again
            </a>.
              Otherwise, please download it below, open it up, and try again.
          </h5>
        </Show>
        <Show when={!showFailureToOpenAppDialog}>
          <h4 className={`${block}__dialog-title`}>Get the Desktop App!</h4>
          <h5 className={`${block}__dialog-subtitle`}>
            Download and install the desktop app to play Amplio songs.<br /><br />
            The Amplio app is both a music player and a research tool that helps
            us find out how Deaf and hard of hearing people access music. We will
            use what we learn to improve Amplio, to ensure artists get paid, and
            also to advise the wider music industry about how to make their music
            more inclusive.
          </h5>
        </Show>
        <h5 className={`${block}__dialog-subtitle`}>
            In addition to agreeing to the
            site's <a href="/terms-and-conditions" target='_blank'>Terms and Conditions</a> and
            the <a href="/privacy-policy" target='_blank'>Privacy Policy</a>,
            you agree to:
        </h5>

        <div className={`${block}__terms`}>
          <div className={`${block}__term`}>
            <StaticImage
              placeholder="blurred"
              className={`${block}__term-icon`}
              alt="Term and condition icon"
              src="../../assets/svg/checkmark.svg"
            />
            <div className={`${block}__term-text`}>
              <h5 className={`${block}__term-title`}>
                <b>Give us permission to track which songs you play and their settings.</b>
              </h5>
            </div>
          </div>
          <div className={`${block}__term`}>
            <StaticImage
              placeholder="blurred"
              className={`${block}__term-icon`}
              alt="Term and condition icon"
              src="../../assets/svg/checkmark.svg"
            />
            <div className={`${block}__term-text`}>
              <h5 className={`${block}__term-title`}>
                <b>Give us permission to track which features you use.</b>
              </h5>
            </div>
          </div>
          <div className={`${block}__term`}>
            <StaticImage
              placeholder="blurred"
              className={`${block}__term-icon`}
              alt="Term and condition icon"
              src="../../assets/svg/checkmark.svg"
            />
            <div className={`${block}__term-text`}>
              <h5 className={`${block}__term-title`}>
                <b>Play songs for your personal use only</b>
              </h5>
              <h6 className={`${block}__term-subtitle`}>
                If you wish to play an Amplio song at a public event, you will
                need to obtain licences for the songs from your local copyright
                collection society. You will not need a license to display the app.
              </h6>
            </div>
          </div>
          <div className={`${block}__term`}>
            <StaticImage
              placeholder="blurred"
              className={`${block}__term-icon`}
              alt="Term and condition icon"
              src="../../assets/svg/checkmark.svg"
            />
            <div className={`${block}__term-text`}>
              <h5 className={`${block}__term-title`}>
                <b>Monitor the use of songs by children</b>
              </h5>
              <h6 className={`${block}__term-subtitle`}>
                It is the responsibility of parents and teachers to ensure the
                appropriateness of songs as some songs may contain mature content.
              </h6>
            </div>
          </div>
        </div>
        <div className={`${block}__buttons-wrap`}>
          <button className="secondary" onClick={onDisagree}>
              Disagree
          </button>
          <button className={`${block}__agree-button`} onClick={onTermsAgree}>
              Agree and Download
          </button>
        </div>
      </Show>
    </ReusableDialog>
  );
};
